import { ChevronRight, Dumbbell, LayoutList, ListChecks, User } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { CLIENT_ID } from '@/api/client'
import { getActiveSession, getTemplates, startSession } from '@/api/services/workout'
import type { WorkoutTemplate } from '@/api/types/workout'
import { BodyMetricsSummary } from '@/components/shared/body-metrics-summary'
import { ExerciseAdviceCard } from '@/components/shared/exercise-advice-card'
import { Header } from '@/components/shared/header'
import { SwipeTabs } from '@/components/shared/swipe-tabs'
import { Button } from '@/components/ui/button'
import { useQueryTabState } from '@/hooks/useQueryTabState'
import { useSyncEventListener } from '@/hooks/useSyncEventListener'
import { useAppStore } from '@/store/useAppStore'
import { useSleepStore } from '@/store/useSleepStore'

import { ExercisesManager } from './components/exercises-manager'
import { MuscleSummaryCard } from './components/muscle-summary-card'
import { ProfileSettings } from './components/profile-settings'
import { SessionHistory } from './components/session-history'
import { TemplateStartList } from './components/template-start-list'
import { TemplatesManager } from './components/templates-manager'
import { WORKOUT_TAB_VALUES, WORKOUT_TABS, type WorkoutTab } from './workout-tabs'

/** 睡眠・HRV・Body Battery から決めた、今日の運動の強さ */
const TodayExerciseAdvice = () => {
  const hasFullAccess = useAppStore((s) => s.hasFullAccess)
  const advice = useSleepStore((s) => s.plan?.exercise)
  const fetchPlan = useSleepStore((s) => s.fetchPlan)

  useEffect(() => {
    if (hasFullAccess) void fetchPlan(true)
  }, [hasFullAccess, fetchPlan])

  if (!hasFullAccess || !advice) return null
  return <ExerciseAdviceCard advice={advice} />
}

export const WorkoutHomePage = () => {
  const navigate = useNavigate()
  const startLoading = useAppStore((s) => s.startLoading)
  const stopLoading = useAppStore((s) => s.stopLoading)
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([])
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [showExercises, setShowExercises] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  // セッション画面から戻ったときに同じタブを開けるよう URL (?tab=) に残す（履歴は増やさない）
  const [tab, setTab] = useQueryTabState<WorkoutTab>('tab', WORKOUT_TAB_VALUES, 'today')

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [t, active] = await Promise.all([getTemplates(false), getActiveSession()])
      setTemplates(t)
      setActiveSessionId(active?.id ?? null)
    } catch (err) {
      console.error(err)
      toast.error('データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  // 他端末での変更を SSE で受信して自動反映
  useSyncEventListener(
    useCallback(
      (event) => {
        if (
          event.type !== 'workout.session.changed' &&
          event.type !== 'workout.template.changed' &&
          event.type !== 'workout.exercise.changed'
        ) {
          return
        }
        if (event.origin === CLIENT_ID) return
        void reload()
      },
      [reload],
    ),
  )

  const handleSessionDeleted = useCallback((sessionId: number) => {
    setActiveSessionId((prev) => (prev === sessionId ? null : prev))
  }, [])

  const handleStartFromTemplate = useCallback(
    async (template: WorkoutTemplate) => {
      startLoading()
      try {
        const session = await startSession({ template_id: template.id, name: template.name })
        navigate(`/workout/sessions/${session.id}`)
      } catch (err) {
        console.error(err)
        toast.error('セッション開始に失敗しました')
      } finally {
        stopLoading()
      }
    },
    [navigate, startLoading, stopLoading],
  )

  if (showExercises) {
    return <ExercisesManager onClose={() => setShowExercises(false)} />
  }
  if (showTemplates) {
    return (
      <TemplatesManager
        onClose={() => {
          setShowTemplates(false)
          void reload()
        }}
        onOpenProfile={() => {
          setShowTemplates(false)
          setShowProfile(true)
        }}
      />
    )
  }
  if (showProfile) {
    return <ProfileSettings onClose={() => setShowProfile(false)} />
  }

  const renderPanel = (value: WorkoutTab) => {
    switch (value) {
      case 'today':
        return (
          <div className="mx-auto max-w-2xl space-y-6">
            <TodayExerciseAdvice />
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">テンプレートから開始</h2>
              <TemplateStartList
                templates={templates}
                loading={loading}
                onStart={handleStartFromTemplate}
                onManage={() => setShowTemplates(true)}
              />
            </section>
          </div>
        )
      case 'muscles':
        return (
          <div className="mx-auto max-w-2xl">
            <MuscleSummaryCard />
          </div>
        )
      case 'history':
        return (
          <div className="mx-auto max-w-2xl">
            <SessionHistory onDeleted={handleSessionDeleted} />
          </div>
        )
      case 'body':
        return (
          <div className="mx-auto max-w-2xl">
            <BodyMetricsSummary title="体組成サマリ" />
          </div>
        )
    }
  }

  const menuSections = [
    {
      type: 'action' as const,
      label: '管理',
      items: [
        {
          key: 'templates',
          label: 'テンプレート管理',
          icon: <LayoutList className="size-4" />,
          onClick: () => setShowTemplates(true),
        },
        {
          key: 'exercises',
          label: '種目マスタ',
          icon: <ListChecks className="size-4" />,
          onClick: () => setShowExercises(true),
        },
        {
          key: 'profile',
          label: 'トレーニング設定',
          icon: <User className="size-4" />,
          onClick: () => setShowProfile(true),
        },
      ],
    },
  ]

  return (
    <div className="flex h-dvh flex-col bg-background">
      <Header title="筋トレ" onClose={() => navigate('/')} dropdownSections={menuSections} />
      <SwipeTabs<WorkoutTab>
        idPrefix="workout-tab"
        ariaLabel="今日・部位・履歴・体組成の切り替え"
        tabs={WORKOUT_TABS}
        activeValue={tab}
        onChange={setTab}
        renderPanel={renderPanel}
        // 再開バーがあるときはバー側でセーフエリアを空ける
        panelClassName={activeSessionId ? 'p-4' : 'px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))]'}
      />

      {activeSessionId && (
        <div className="border-t bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button
            className="h-12 w-full justify-start gap-3 bg-orange-500 text-white hover:bg-orange-600"
            onClick={() => navigate(`/workout/sessions/${activeSessionId}`)}
          >
            <Dumbbell className="size-5" />
            <span className="flex-1 text-left font-semibold">進行中のセッションを再開</span>
            <ChevronRight className="size-5" />
          </Button>
        </div>
      )}
    </div>
  )
}
